// Gallery Component - Shared (portal + microsites)

// Constants
const ORIENTATION_CHANGE_DELAY = 100;
const POSITION_CHANGE_THRESHOLD = 50;
const LAYOUT_SETTLE_DELAY = 100;
const VIDEO_LAYOUT_SETTLE_DELAY = 200;
const AUTO_INIT_DELAY = 1000;

const galleryComponent = {
    component: null,
    scEditorMode: null,
    galleries: [],
    selectedGallery: 0,
    mobileInitialised: false,
    desktopInitialised: false
};

// Initialize component if it exists
const initializeComponent = () => {
    galleryComponent.component = document.querySelector('.gallery-component');
    if (!galleryComponent.component) return false;
    galleryComponent.scEditorMode = document.querySelector('.sc-editor-mode');
    return true;
};

// Initialize gallery headers and structure
const initializeGalleryHeaders = () => {
    const galleryHeaders = document.querySelectorAll('.gallery-header');
    if (!galleryHeaders.length) return;
    getGalleryStructure();
    if (!galleryComponent.scEditorMode) {
        setupInitialGallerySelection(galleryHeaders);
    }
    setupGalleryTabClickHandlers();
};

// Setup initial gallery selection on page load
const setupInitialGallerySelection = (galleryHeaders) => {
    galleryHeaders.forEach((header) => {
        const firstTabHeaderLinks = header.firstElementChild;
        const firstLink = firstTabHeaderLinks?.firstElementChild;
        if (firstLink) {
            const selectGallery = () => setSelectedGallery(header, firstLink);
            if (document.readyState !== 'complete') {
                window.addEventListener('load', selectGallery);
            } else {
                selectGallery();
            }
        }
    });
};

// Setup click handlers for gallery tab links
const setupGalleryTabClickHandlers = () => {
    const handleGalleryLinkClick = (e) => {
        const galleryHeader = e.target.parentElement.parentElement;
        setSelectedGallery(galleryHeader, e.target);
    };
    const galleryTabLinks = document.getElementsByClassName('gallery-tab-link');
    Array.from(galleryTabLinks).forEach((btn) =>
        btn.addEventListener('click', handleGalleryLinkClick, false)
    );
};

// Initialize the appropriate view based on screen size
const initializeCurrentView = () => {
    if (mobileGalleryView()) {
        initializeMobileGallery();
    } else {
        initializeDesktopGallery();
    }
};

// Setup orientation change handling
const setupOrientationChangeHandler = () => {
    const orientationChangeHandler = () => {
        setTimeout(() => {
            handleOrientationChange();
        }, ORIENTATION_CHANGE_DELAY);
    };
    if (windowUtils.addOrientationListener) {
        windowUtils.addOrientationListener(orientationChangeHandler);
    } else {
        window.addEventListener('orientationchange', orientationChangeHandler);
        window.addEventListener('resize', orientationChangeHandler);
    }
};
// Handle orientation/resize changes
const handleOrientationChange = () => {
    if (mobileGalleryView()) {
        handleMobileViewChange();
    } else {
        handleDesktopViewChange();
    }
};

// Handle change to mobile view
const handleMobileViewChange = () => {
    if (!galleryComponent.mobileInitialised) {
        initializeMobileGallery();
        return;
    }
    const galleryContent = getSelectedGalleryContent();
    if (!galleryContent) return;
    const gallerySelect = galleryContent.querySelector('.gallery-select');
    const gallerySelectComponent = galleryContent.querySelector('.gallery-select-component');
    if (gallerySelect && gallerySelectComponent) {
        gallerySelect.selectedIndex = getCurrentSelectedTab();
        setActivetMobileGalleryTab(gallerySelectComponent);
    }
};

// Handle change to desktop view
const handleDesktopViewChange = () => {
    if (!galleryComponent.desktopInitialised) {
        initializeDesktopGallery();
        return;
    }
    const tab = getActiveDesktopTab();
    if (tab) {
        setActiveGalleryTab(tab);
    }
};

// Get the currently selected gallery content element
const getSelectedGalleryContent = () => {
    const selectedGallery = galleryComponent.galleries[galleryComponent.selectedGallery];
    if (!selectedGallery) return null;
    return document.querySelector(`.gallery-tab-header-content[data-tab="${selectedGallery.galleryTab}"]`);
};

// Get current selected tab index
const getCurrentSelectedTab = () => {
    const selectedGallery = galleryComponent.galleries[galleryComponent.selectedGallery];
    return selectedGallery ? selectedGallery.selectedTab : 0;
};

// Get active desktop tab element
const getActiveDesktopTab = () => {
    const galleryComponentHeader = galleryComponent.component?.querySelector('.gallery-header');
    const selectedGallery = galleryComponent.galleries[galleryComponent.selectedGallery];
    if (!galleryComponentHeader || !selectedGallery) return null;
    const selectedTab = selectedGallery.tabs[selectedGallery.selectedTab];
    if (!selectedTab) return null;
    return galleryComponentHeader.querySelector(`.tablinks[data-id*="${selectedTab.id}"]`);
};

// Build gallery structure from DOM
const getGalleryStructure = () => {
    const galleryTablinks = galleryComponent.component.querySelectorAll('.gallery-header .gallery-tab-link');
    galleryTablinks.forEach((tablink) => {
        const gallery = createGalleryFromTablink(tablink);
        galleryComponent.galleries.push(gallery);
    });
};

// Create gallery object from tablink element
const createGalleryFromTablink = (tablink) => {
    const gallery = {
        galleryTab: tablink.dataset.tab,
        selectedTab: 0,
        tabs: []
    };
    const galleryTab = document.querySelector(`.gallery-tab-header-content[data-tab="${tablink.dataset.tab}"]`);
    if (galleryTab) {
        const galleryTabLinks = galleryTab.querySelectorAll('.gallery-links-wrapper .tablinks');
        gallery.tabs = Array.from(galleryTabLinks).map(link => ({ id: link.dataset.id }));
    }
    return gallery;
};

// Set selected gallery
const setSelectedGallery = (galleryComponentHeader, selectedGalleryLink) => {
    updateSelectedGalleryIndex(selectedGalleryLink);
    updateGallerySelectionUI(galleryComponentHeader, selectedGalleryLink);
    handleGalleryContentSelection(galleryComponentHeader, selectedGalleryLink);
};

// Update selected gallery index
const updateSelectedGalleryIndex = (selectedGalleryLink) => {
    galleryComponent.selectedGallery = galleryComponent.galleries.findIndex(
        gallery => gallery.galleryTab === selectedGalleryLink.dataset.tab
    );
};

// Update gallery selection UI
const updateGallerySelectionUI = (galleryComponentHeader, selectedGalleryLink) => {
    // Remove selected class from all buttons
    const selectedElements = galleryComponentHeader.querySelectorAll('.selected');
    selectedElements.forEach(el => el.classList.remove('selected'));
    // Add selected class to current button
    selectedGalleryLink.classList.add('selected');
    // Add selected class to corresponding content
    const galleryTabContent = galleryComponentHeader.querySelector(
        `div.gallery-tab-header-content[data-tab*="${selectedGalleryLink.dataset.tab}"]`
    );
    if (galleryTabContent) {
        galleryTabContent.classList.add('selected');
    }
};

// Handle gallery content selection based on view
const handleGalleryContentSelection = (galleryComponentHeader, selectedGalleryLink) => {
    const galleryContent = galleryComponentHeader.querySelector(
        `div.gallery-tab-header-content[data-tab*="${selectedGalleryLink.dataset.tab}"]`
    );
    if (!galleryContent) return;
    if (mobileGalleryView()) {
        handleMobileGallerySelection(galleryContent);
    } else {
        handleDesktopGallerySelection();
    }
};

// Handle mobile gallery selection
const handleMobileGallerySelection = (galleryContent) => {
    const gallerySelect = galleryContent.querySelector('.gallery-select');
    const gallerySelectComponent = galleryContent.querySelector('.gallery-select-component');
    if (gallerySelect && gallerySelectComponent) {
        gallerySelect.selectedIndex = getCurrentSelectedTab();
        setActivetMobileGalleryTab(gallerySelectComponent);
    }
};

// Handle desktop gallery selection
const handleDesktopGallerySelection = () => {
    const tab = getActiveDesktopTab();
    if (tab) {
        setActiveGalleryTab(tab);
    }
};

// Set active gallery tab (desktop)
const setActiveGalleryTab = (activeTab) => {
    updateTabActiveState(activeTab);
    const activeTabContent = updateTabContentActiveState(activeTab);
    if (activeTabContent) {
        initializeGalleriesInTab(activeTabContent);
        updateSelectedTabIndex(activeTab);
    }
};

// Update tab active state
const updateTabActiveState = (activeTab) => {
    const galleryTabComponent = activeTab.parentElement;
    // Clear active state from all tabs
    const activeTabs = galleryTabComponent.querySelectorAll('.active');
    activeTabs.forEach(tab => tab.classList.remove('active'));
    // Set active state on current tab
    activeTab.classList.add('active');
};

// Update tab content active state and return active content
const updateTabContentActiveState = (activeTab) => {
    const galleryTabComponent = activeTab.parentElement;
    const galleryWrapper = galleryTabComponent.parentElement.querySelector('.tab-content-wrapper');
    if (!galleryWrapper) return null;
    // Clear active state from all content
    const activeContent = galleryWrapper.querySelectorAll('.active');
    activeContent.forEach(content => content.classList.remove('active'));
    // Set active state on current content
    const activeTabContent = galleryWrapper.querySelector(`div.tab-item-content[data-id*="${activeTab.dataset.id}"]`);
    if (activeTabContent) {
        activeTabContent.classList.add('active');
    }
    return activeTabContent;
};

// Initialize galleries in the active tab
const initializeGalleriesInTab = (activeTabContent) => {
    const galleryLayouts = activeTabContent.querySelectorAll('.gallery-layout:not(.lg-initialised)');
    galleryLayouts.forEach(galleryLayout => {
        const galleryItems = galleryLayout.querySelectorAll('.gallery-item');
        if (galleryItems.length > 0) {
            initializeLightGallery(Array.from(galleryItems));
        }
    });
};

// Update selected tab index
const updateSelectedTabIndex = (activeTab) => {
    const selectedGallery = galleryComponent.galleries[galleryComponent.selectedGallery];
    if (selectedGallery) {
        selectedGallery.selectedTab = selectedGallery.tabs.findIndex(
            tab => tab.id === activeTab.dataset.id
        );
    }
};

// Initialize desktop gallery
const initializeDesktopGallery = () => {
    setupTabCountLabels();
    setupInitialActiveTab();
    setupTabClickHandlers();
    applyCenteringToSmallGalleries();
    galleryComponent.desktopInitialised = true;
};

// Setup tab count labels
const setupTabCountLabels = () => {
    const simpleLinks = document.querySelectorAll('.simple-link');
    if (!simpleLinks.length) return;
    const addLabels = () => addCountToTabLabels();
    if (document.readyState !== 'complete') {
        window.addEventListener('load', addLabels);
    } else {
        addLabels();
    }
};

// Setup initial active tab for desktop
const setupInitialActiveTab = () => {
    if (galleryComponent.scEditorMode) return;
    const galleryComponentHeader = galleryComponent.component?.querySelector('.gallery-header');
    const tab = getActiveDesktopTab();
    if (galleryComponentHeader && tab) {
        const activateTab = () => setActiveGalleryTab(tab);
        if (document.readyState !== 'complete') {
            window.addEventListener('load', activateTab);
        } else {
            activateTab();
        }
    }
};

// Setup tab click handlers
const setupTabClickHandlers = () => {
    const handleClick = (e) => setActiveGalleryTab(e.target);
    const tablinks = galleryComponent.component.querySelectorAll('.tablinks');
    tablinks.forEach(tablink => {
        tablink.addEventListener('click', handleClick, false);
    });
};

// Apply centering to galleries with 10 or fewer items
const applyCenteringToSmallGalleries = () => {
    const mediaContent = document.querySelectorAll('.mobile-hidden .gallery-layout');
    if (!mediaContent.length) return;
    const galleryWrappers = document.querySelectorAll('.gallery-links-wrapper');
    galleryWrappers.forEach(wrapper => {
        const childCount = wrapper.childElementCount;
        // Check if childCount is less than or equal to 10
        if (childCount <= 10) {
            wrapper.style.justifyContent = 'center';
        }
    });
};

// Initialize mobile gallery
const initializeMobileGallery = () => {
    setupMobileGalleryDropdowns();
    initializeMobileGalleryLayouts();
    galleryComponent.mobileInitialised = true;
};

// Setup mobile gallery dropdowns
const setupMobileGalleryDropdowns = () => {
    const gallerySelects = document.querySelectorAll('.gallery-select-component');
    if (!gallerySelects.length) return;
    const setupVisibility = () => setFirstTabItemsVisible();
    if (document.readyState !== 'complete') {
        window.addEventListener('load', setupVisibility);
    } else {
        setupVisibility();
    }
    gallerySelects.forEach(galSelComp => {
        const handleChange = () => setActivetMobileGalleryTab(galSelComp);
        const selects = galSelComp.getElementsByClassName('gallery-select');
        Array.from(selects).forEach(select => {
            select.addEventListener('change', handleChange, false);
        });
    });
};

// Initialize mobile gallery layouts
const initializeMobileGalleryLayouts = () => {
    const mediaContent = document.querySelectorAll('.desktop-hidden .gallery-layout');
    mediaContent.forEach(media => {
        const initLightboxEl = media.querySelector('.grid');
        if (initLightboxEl) {
            const galleryItems = initLightboxEl.querySelectorAll('.gallery-item');
            if (galleryItems.length > 0) {
                initializeLightGallery(Array.from(galleryItems));
            }
        }
    });
};

// Set active mobile gallery tab
const setActivetMobileGalleryTab = (gallerySelectComponent) => {
    const selectContent = gallerySelectComponent.querySelectorAll('.select-content');
    const gallerySelect = gallerySelectComponent.querySelector('.gallery-select');
    if (!gallerySelect) return;
    // Hide all content
    selectContent.forEach(content => content.classList.remove('visible'));
    // Show selected content
    const selectedContent = selectContent[gallerySelect.selectedIndex];
    if (selectedContent) {
        selectedContent.classList.add('visible');
    }
    // Update selected tab index
    const selectedGallery = galleryComponent.galleries[galleryComponent.selectedGallery];
    if (selectedGallery) {
        selectedGallery.selectedTab = gallerySelect.selectedIndex;
    }
};

// Set first tab items visible
const setFirstTabItemsVisible = () => {
    const selectedGalleryContent = getSelectedGalleryContent();
    if (!selectedGalleryContent) return;
    const selectedIndex = getCurrentSelectedTab();
    const select = selectedGalleryContent.querySelector('.gallery-select');
    if (select) {
        select.selectedIndex = selectedIndex;
    }
    const selectContentList = selectedGalleryContent.querySelectorAll('.select-content-list .select-content');
    const selectedContent = selectContentList[selectedIndex];
    if (selectedContent) {
        selectedContent.classList.add('visible');
    }
};

// Add count to tab labels
const addCountToTabLabels = () => {
    const simpleLinks = document.getElementsByClassName('simple-link');
    Array.from(simpleLinks).forEach(btn => {
        const count = document.querySelectorAll(
            `div.tab-item-content[data-id="${btn.dataset.id}"] .gallery-item`
        ).length;
        btn.appendChild(document.createTextNode(` (${count})`));
    });
};

// Check if mobile gallery view
const mobileGalleryView = () => {
    return window.matchMedia("(max-width: 1023px)").matches;
};

// Initialize LightGallery for gallery items
const initializeLightGallery = (galleryItems) => {
    if (!galleryItems?.length) {
        return;
    }
    const galleryContainer = galleryItems[0].closest('.gallery-layout');
    if (!galleryContainer) {
        return;
    }
    if (galleryContainer.classList.contains('lg-initialised')) {
        return;
    }
    
    // Check if we're in mobile view (select-content structure)
    const selectContent = galleryContainer.closest('.select-content');
    if (selectContent) {
        // Mobile: Get only gallery items from THIS select-content div
        const galleryItemsInThisContent = Array.from(selectContent.querySelectorAll('.gallery-item'));
        try {
            const lg = createLightGalleryInstance(galleryContainer, galleryItemsInThisContent);
            markGalleryAsInitialized(galleryContainer, lg);
            // Mark only layouts in THIS select-content as initialized
            const layoutsInThisContent = selectContent.querySelectorAll('.gallery-layout');
            layoutsInThisContent.forEach(layout => layout.classList.add('lg-initialised'));
        } catch (error) {
            console.error('Error initializing LightGallery v2:', error);
        }
    } else {
        // Desktop: Get all gallery items from ALL gallery-layout divs in the same slider wrapper
        const sliderWrapper = galleryContainer.closest('.simple-slider-wrapper');
        if (sliderWrapper) {
            // Get ALL gallery items from ALL gallery-layout containers in this slider
            const allGalleryItems = Array.from(sliderWrapper.querySelectorAll('.gallery-item'));
            try {
                const lg = createLightGalleryInstance(galleryContainer, allGalleryItems);
                markGalleryAsInitialized(galleryContainer, lg);
                // Mark ALL layouts in the slider wrapper as initialized
                const allLayouts = sliderWrapper.querySelectorAll('.gallery-layout');
                allLayouts.forEach(layout => {
                    layout.classList.add('lg-initialised');
                    layout.lgInstance = lg; // Store reference to shared LightGallery instance
                });
            } catch (error) {
                console.error('Error initializing LightGallery v2:', error);
            }
        } else {
            // Fallback to original behavior
            try {
                const lg = createLightGalleryInstance(galleryContainer);
                markGalleryAsInitialized(galleryContainer, lg);
            } catch (error) {
                console.error('Error initializing LightGallery v2:', error);
            }
        }
    }
};

// Create LightGallery instance
const createLightGalleryInstance = (galleryContainer, allGalleryItems = null) => {
    const config = {
        actualSize: true,
        actualSizeIcons: {
            zoomIn: 'lg-actual-size',
            zoomOut: 'lg-actual-size'
        },
        download: false,
        fullScreen: true,
        getCaptionFromTitleOrAlt: true,
        licenseKey: '40A61D45-65C0468F-B1B39175-A739D8FD',
        mobileSettings: {
            actualSize: true,
            actualSizeIcons: {
                zoomIn: 'lg-actual-size',
                zoomOut: 'lg-actual-size'
            },
            controls: true,
            showCloseIcon: true,
            showZoomInOutIcons: true,
        },
        mode: 'lg-slide',
        plugins: [lgVideo, lgFullscreen, lgZoom, lgThumbnail],
        share: false,
        showZoomInOutIcons: true,
        subHtmlSelectorRelative: true,
        thumbnail: true,
        videoMaxSize: '854-480',
        zoom: true,
        zoomFromOrigin: false
    };

    // If we have all gallery items, use dynamic mode
    if (allGalleryItems && allGalleryItems.length > 0) {
        config.dynamic = true;
        config.dynamicEl = allGalleryItems.map(item => {
            const el = {
                src: item.dataset.src || item.href,
                thumb: item.querySelector('img')?.src || '',
                subHtml: item.dataset.subHtml || ''
            };
            // Add iframe property for virtual tours and other iframe content
            if (item.dataset.iframe === 'true') {
                el.iframe = true;
            }
            return el;
        });
    } else {
        config.selector = '.gallery-item';
    }

    const lg = lightGallery(galleryContainer, config);

    // If using dynamic mode, manually bind click events
    if (allGalleryItems && allGalleryItems.length > 0) {
        allGalleryItems.forEach((item, index) => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                lg.openGallery(index);
            });
        });
    }

    // Initialize Vimeo Player stub to prevent console errors
    videoUtils.initVimeoPlayerStub();

    // Store gallery container position for scroll restoration after orientation changes
    let galleryScrollPosition = null;

    // Add event listeners
    if (lg.LGel && typeof lg.LGel.on === 'function') {
        // Save gallery anchor reference and scroll position when gallery opens
        lg.LGel.on('lgBeforeOpen', () => {
            // Find the gallery component and look for nearby anchor with ID
            const galleryComponent = galleryContainer.closest('.gallery-component');
            if (galleryComponent) {
                // Look for anchor tag with ID near the gallery component
                const anchorElement = galleryComponent.querySelector('a[id]') ||
                                    galleryComponent.previousElementSibling?.querySelector('a[id]') ||
                                    galleryComponent.parentElement?.querySelector('a[id]');

                if (anchorElement?.id) {
                    galleryScrollPosition = {
                        anchorId: anchorElement.id,
                        initialScrollY: window.scrollY,
                        initialAnchorTop: anchorElement.getBoundingClientRect().top + window.scrollY
                    };
                }
            }
        });

        // Restore scroll position to gallery anchor when gallery closes (only if layout changed)
        lg.LGel.on('lgAfterClose', () => {
            if (galleryScrollPosition?.anchorId) {
                // Small delay to ensure layout has settled after close
                setTimeout(() => {
                    // Find the anchor element and check if its position changed
                    const anchorElement = document.getElementById(galleryScrollPosition.anchorId);
                    if (anchorElement) {
                        const currentAnchorTop = anchorElement.getBoundingClientRect().top + window.scrollY;
                        const positionChanged = Math.abs(currentAnchorTop - galleryScrollPosition.initialAnchorTop) > POSITION_CHANGE_THRESHOLD;

                        // Only scroll if the anchor position changed significantly (layout change)
                        if (positionChanged) {
                            anchorElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                            });
                        }
                        // If position didn't change much, user stays where they are
                    }
                }, LAYOUT_SETTLE_DELAY);
            }
        });

        lg.LGel.on('lgHasVideo', () => {
            // Force HTTPS on iframe after it's created
            setTimeout(() => {
                const iframe = document.querySelector('.lg-video-object');
                // Apply sandbox attributes for security (LightGallery doesn't add them by default)
                if (iframe && !iframe.hasAttribute('sandbox')) {
                    videoUtils.sandboxVideos('.lg-video-object');
                }
            }, VIDEO_LAYOUT_SETTLE_DELAY);
        });
    }
    return lg;
};

// Mark gallery as initialized
const markGalleryAsInitialized = (galleryContainer, lg) => {
    galleryContainer.classList.add('lg-initialised');
    galleryContainer.lgInstance = lg;
};

// Auto-initialize galleries on page load
const autoInitializeGalleries = () => {
    const visibleLayouts = document.querySelectorAll('.gallery-layout:not(.lg-initialised)');
    visibleLayouts.forEach((layout) => {
        const items = layout.querySelectorAll('.gallery-item');
        if (items.length > 0) {
            initializeLightGallery(Array.from(items));
        }
    });
};

// Defer init until after the concatenated bundle finishes evaluating.
// This file sorts before videoUtils/windowUtils alphabetically in src/shared/js/,
// so synchronous top-level init would hit temporal dead zone errors.
const startGalleryComponent = () => {
    if (initializeComponent()) {
        initializeGalleryHeaders();
        initializeCurrentView();
        setupOrientationChangeHandler();
    }
    setTimeout(autoInitializeGalleries, AUTO_INIT_DELAY);
};

queueMicrotask(startGalleryComponent);
